import { useEffect, PropsWithoutRef, ComponentType } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useAuthStore } from "@stores"
import { flattenRoutesTreeAndAddSelectKey, normalizePathname } from "@utils/helpers"
import { withQueryClient } from "@hocs/withQueryClient"
import { ROUTE, ROUTES } from "@constants"

export default function withAuth<P extends object>(WrappedComponent: ComponentType<P>) {
  const AuthComponent = (props: PropsWithoutRef<unknown>) => {
    const navigate = useNavigate()
    const pathname = useLocation().pathname
    const accessToken = useAuthStore((state) => state.access_token)
    const userPermissions = useAuthStore((state) => state.permissions)
    const getUserInfo = useAuthStore((state) => state.actions.getUserInfo)
    const userInfo = useAuthStore((state) => state.user)

    const { refetch, isSuccess } = useQuery({
      queryFn: getUserInfo,
      queryKey: ["user-info"],
      select: ({ data = {} }) => data,
      enabled: false,
    })

    useEffect(() => {
      if (!accessToken) {
        navigate(ROUTE.LOGIN, { replace: true })
      } else {
        refetch()
      }
    }, [accessToken, pathname])

    useEffect(() => {
      if (userInfo.id && isSuccess) {
        const flattenRoutes = flattenRoutesTreeAndAddSelectKey(ROUTES)
        const routeInfo = flattenRoutes.find((item) => normalizePathname(item.path) === normalizePathname(pathname))

        if (routeInfo?.permission && !userPermissions[routeInfo.permission]) {
          navigate(ROUTE.FORBIDDEN, { replace: true })
        }
      }
    }, [isSuccess, pathname, userInfo])

    if (!accessToken || !isSuccess) return null

    return <WrappedComponent {...(props as P)} />
  }

  return withQueryClient(AuthComponent)
}
